import type { ReactNode } from "react";
import type { TFunction } from "i18next";
import { TooltipBadge } from "@/components/shared/TooltipBadge";
import type { AppBindingResponse, AppBindingSummary } from "@/types/api";
import { cn } from "@/lib/utils";

type StatusTone = "success" | "pending" | "error" | "inactive";

interface StatusBadgeView {
  label: string;
  tone: StatusTone;
  ariaText: string;
  hint: ReactNode;
}

interface BindingStatusView {
  primary: StatusBadgeView;
  attentions: StatusBadgeView[];
}

export type AppAvailabilityState =
  | "available"
  | "setup_incomplete"
  | "needs_action"
  | "unavailable"
  | "needs_check";

export const APP_AVAILABILITY_FILTERS: AppAvailabilityState[] = [
  "available",
  "setup_incomplete",
  "needs_action",
  "unavailable",
  "needs_check",
];

export function isAppAvailabilityFilter(
  value: string | null,
): value is AppAvailabilityState {
  return (
    typeof value === "string" &&
    APP_AVAILABILITY_FILTERS.includes(value as AppAvailabilityState)
  );
}

export type AppSettingState = "ready" | "pending" | "blocked" | "unknown";
export type AppRuntimeState =
  | "available"
  | "unavailable"
  | "degraded"
  | "unknown"
  | "not_requested";

type AppBindingStatusSource = Pick<
  AppBindingSummary | AppBindingResponse,
  | "activationState"
  | "appSettingState"
  | "availabilityState"
  | "blockedReason"
  | "currentPluginVersion"
  | "latestPluginVersion"
  | "pluginVersionState"
  | "runtimeStatusSnapshot"
  | "settingReflectionState"
>;

export function resolveAvailabilityState(
  b: AppBindingStatusSource,
): AppAvailabilityState {
  return b.availabilityState ?? "needs_check";
}

export function resolveAppSettingState(
  b: AppBindingStatusSource,
): AppSettingState {
  return b.appSettingState ?? "unknown";
}

export function resolveAppRuntimeState(
  b: AppBindingStatusSource,
): AppRuntimeState {
  return b.runtimeStatusSnapshot?.runtimeStatusState ?? "unknown";
}

function buildPendingSteps(t: TFunction, b: AppBindingStatusSource) {
  const steps: string[] = [];
  if (b.activationState === "activation_pending") {
    steps.push(t("readinessHints.activation_pending"));
  } else if (b.settingReflectionState === "plugin_reapply_pending") {
    steps.push(t("readinessHints.plugin_reapply_pending"));
  } else if (b.settingReflectionState === "service_saved") {
    steps.push(t("readinessHints.service_saved"));
  }
  if (steps.length === 0) {
    steps.push(t("readinessHints.pending_default"));
  }
  return steps;
}

function buildAvailability(
  t: TFunction,
  b: AppBindingStatusSource,
): StatusBadgeView {
  const availabilityState = resolveAvailabilityState(b);
  if (availabilityState === "available") {
    const text = t("availabilityHints.available");
    return {
      label: t("availabilityLabels.available"),
      tone: "success",
      ariaText: text,
      hint: text,
    };
  }

  if (availabilityState === "setup_incomplete") {
    const steps = buildPendingSteps(t, b);
    return {
      label: t("availabilityLabels.setup_incomplete"),
      tone: "pending",
      ariaText: `${t("availabilityHints.nextActionTitle")}: ${steps.join(" / ")}`,
      hint: (
        <div>
          <div className="mb-0.5 font-semibold">
            {t("availabilityHints.nextActionTitle")}
          </div>
          {steps.length > 1 ? (
            <ol className="list-decimal space-y-0.5 pl-4">
              {steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          ) : (
            <div>{steps[0]}</div>
          )}
        </div>
      ),
    };
  }

  const fallbackHint =
    availabilityState === "needs_action"
      ? t("availabilityHints.needsActionDefault")
      : availabilityState === "unavailable"
        ? t("availabilityHints.unavailableDefault")
        : t("availabilityHints.needsCheckDefault");
  const reasonCode = b.blockedReason ?? b.runtimeStatusSnapshot?.reasonCode ?? null;
  const reason = reasonCode
    ? t(`errors:blockedReasons.${reasonCode}`, {
        defaultValue: fallbackHint,
      })
    : fallbackHint;
  const tone: StatusTone =
    availabilityState === "needs_check" ? "pending" : "error";
  return {
    label: t(`availabilityLabels.${availabilityState}`),
    tone,
    ariaText: `${t("availabilityHints.stopReasonTitle")}: ${reason}`,
    hint: (
      <div>
        <div className="mb-0.5 font-semibold">
          {t("availabilityHints.stopReasonTitle")}
        </div>
        <div>{reason}</div>
      </div>
    ),
  };
}

function buildBindingAttentions(
  t: TFunction,
  b: AppBindingStatusSource,
): StatusBadgeView[] {
  const attentions: StatusBadgeView[] = [];

  if (
    resolveAvailabilityState(b) === "available" &&
    b.settingReflectionState === "plugin_reapply_pending"
  ) {
    const text = t("attentionHints.unappliedSettings");
    attentions.push({
      label: t("attentionLabels.unappliedSettings"),
      tone: "pending",
      ariaText: text,
      hint: text,
    });
  }

  if (b.pluginVersionState === "update_available") {
    const currentVersion =
      b.currentPluginVersion ?? t("attentionHints.pluginVersionUnknown");
    const text = t("attentionHints.pluginUpdateAvailable", {
      currentVersion,
      latestVersion: b.latestPluginVersion,
    });
    attentions.push({
      label: t("attentionLabels.pluginUpdateAvailable"),
      tone: "pending",
      ariaText: text,
      hint: text,
    });
  }

  return attentions;
}

export function buildBindingStatus(
  t: TFunction,
  b: AppBindingStatusSource,
): BindingStatusView {
  return {
    primary: buildAvailability(t, b),
    attentions: buildBindingAttentions(t, b),
  };
}

export function AppBindingStatusBadges({
  binding,
  className,
  t,
}: {
  binding: AppBindingStatusSource;
  className?: string;
  t: TFunction;
}) {
  const status = buildBindingStatus(t, binding);
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      <TooltipBadge
        label={status.primary.label}
        tone={status.primary.tone}
        hint={status.primary.hint}
        ariaLabel={`${status.primary.label}: ${status.primary.ariaText}`}
      />
      {status.attentions.map((attention) => (
        <TooltipBadge
          key={attention.label}
          label={attention.label}
          tone={attention.tone}
          hint={attention.hint}
          ariaLabel={`${attention.label}: ${attention.ariaText}`}
          className="min-w-0"
        />
      ))}
    </div>
  );
}
